import React, { useEffect, useState } from 'react';
import 'components/new-or-old-contract/new-or-old-contract.scss';
import { Divider, Form, Input, Radio, Select, Typography, Row, Col } from 'antd';
import { InputValidator } from 'fragments/add-property/interfaces';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import moment from 'moment';
import CurrencyInput from 'components/currency-input/currency-input.component';
import { fixSelectOnScroll } from 'tools/view-hooks/fix-autocomplete';

export interface NewOrOldContractProps {
    validate: InputValidator;
    durationMonths: number;
    initialDate: any;
    updateFrequency?: number;
}

const ADJUSTMENT_INDICES = ['ICL', 'IPC', 'UVA', 'RIPTE', 'CAC', 'CER'];

type UpdateMode = 'predefined' | 'periodic';

export interface PeriodicUpdatePeriod {
    order: number;
    label: string;
    startDate: moment.Moment;
    endDate: moment.Moment;
}

export interface CalculatePeriodicUpdatePeriodsParams {
    initialDate: moment.MomentInput;
    durationMonths: number;
    periodicity: number;
    actualDate?: moment.MomentInput;
}

export const calculatePeriodicUpdatePeriods = ({
    initialDate,
    durationMonths,
    periodicity,
    actualDate,
}: CalculatePeriodicUpdatePeriodsParams): PeriodicUpdatePeriod[] => {
    if (periodicity <= 0 || !initialDate || durationMonths <= 0) return [];

    const periods: PeriodicUpdatePeriod[] = [];
    const initialDateContract = moment(initialDate);
    const finishDateContract = moment(initialDate).add(durationMonths, 'M').subtract(1, 'd');
    const currentDate = moment(actualDate);

    let iteration = -1;

    while (initialDateContract.isBefore(finishDateContract)) {
        initialDateContract.add(periodicity, 'M');
        const nextDate = moment(initialDateContract).add(periodicity - 1, 'M');

        if (initialDateContract.isAfter(finishDateContract)) {
            break;
        }

        if (initialDateContract.isAfter(currentDate, 'month')) {
            break;
        }

        iteration = iteration + 1;
        periods.push({
            order: iteration,
            label: `${initialDateContract.month() + 1}/${initialDateContract.year()} al ${
                nextDate.month() + 1
            }/${nextDate.year()}`,
            startDate: moment(initialDateContract),
            endDate: nextDate,
        });
    }

    return periods;
};

const NewOrOldContract: React.FC<NewOrOldContractProps> = (props) => {
    const { translate } = useTranslator();
    const [updateMode, setUpdateMode] = useState<UpdateMode>('periodic');
    const [periodicity, setPeriodicity] = useState(props.updateFrequency ?? 0);
    const [isCustomIndex, setIsCustomIndex] = useState(false);
    const { Option } = Select;

    useEffect(() => {
        if (props.updateFrequency) {
            setPeriodicity(props.updateFrequency);
        }
    }, [props.updateFrequency]);

    const onUpdateModeChange = (e: any) => {
        setUpdateMode(e.target.value);
    };

    const calculateAmountInputs = (value: number) => {
        setPeriodicity(value);
    };

    const onAdjustmentIndexChange = (value: string) => {
        setIsCustomIndex(value === 'other');
    };

    const calculatePredefinedAmounts = (errors?: React.ReactNode[]): { element: React.ReactNode; size: number } => {
        if (periodicity <= 0) return { element: null, size: 0 };
        const initialDateContract = moment(props.initialDate);
        const finishDateContract = moment(props.initialDate).add(props.durationMonths, 'M').subtract(1, 'd');
        const increaseInputs = [];

        let iteration = -1;

        while (initialDateContract.isBefore(finishDateContract)) {
            initialDateContract.add(periodicity, 'M');
            const nextDate = moment(initialDateContract).add(periodicity - 1, 'M');

            if (!nextDate.isBefore(finishDateContract)) {
                continue;
            }
            iteration = iteration + 1;
            const label = `${initialDateContract.month() + 1}/${initialDateContract.year()} al ${
                nextDate.month() + 1
            }/${nextDate.year()}`;

            increaseInputs.push(
                <div key={initialDateContract.milliseconds()}>
                    <Form.Item
                        name={iteration}
                        label={`${translate({ key: 'contracts.update-old' })} ${label}`}
                        labelAlign="right"
                        rules={[{ required: true }]}
                    >
                        <CurrencyInput size={'large'} className="input-rent-amount" placeholder={''} />
                    </Form.Item>
                </div>,
            );
        }
        return {
            element: (
                <div>
                    {increaseInputs}
                    <Typography.Text style={{ color: 'red', marginTop: 0 }}>{errors?.toString()}</Typography.Text>
                </div>
            ),
            size: increaseInputs.length,
        };
    };

    const calculatePeriodicAmounts = (errors?: React.ReactNode[]): { element: React.ReactNode; size: number } => {
        const periods = calculatePeriodicUpdatePeriods({
            initialDate: props.initialDate,
            durationMonths: props.durationMonths,
            periodicity,
        });
        const totalPeriods = periods.length;
        const increaseInputs = periods.map((period, index) => {
            const isCurrentPeriod = index === totalPeriods - 1; // Last period is the current one
            return (
                <div key={`${period.startDate.month()}-${period.startDate.year()}-${period.order}`}>
                    <Form.Item
                        name={period.order}
                        label={`${translate({ key: 'contracts.update-new' })} ${period.label}`}
                        labelAlign="right"
                        rules={[{ required: isCurrentPeriod }]}
                    >
                        <CurrencyInput size={'large'} className="input-rent-amount" placeholder={''} />
                    </Form.Item>
                </div>
            );
        });

        return {
            element: (
                <div>
                    {increaseInputs}
                    <Typography.Text style={{ color: 'red', marginTop: 0 }}>{errors?.toString()}</Typography.Text>
                </div>
            ),
            size: increaseInputs.length,
        };
    };

    const monthLimit = 12;

    return (
        <div className={'new-or-old-contract'}>
            <Row gutter={10} align="bottom">
                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                    <Form.Item
                        name="updateFrequency"
                        label={translate({ key: 'contracts.update-periodicity' })}
                        labelAlign="left"
                        rules={[{ required: true }, { validator: props.validate }]}
                    >
                        <Select
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            size={'middle'}
                            allowClear
                            onChange={calculateAmountInputs}
                            getPopupContainer={fixSelectOnScroll}
                        >
                            {Array.from({ length: monthLimit }, (_, k) => {
                                return (
                                    <Option key={k + 1} value={k + 1}>
                                        {k + 1} {translate({ key: 'contracts.months' })}
                                    </Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} xl={{ span: 12 }}>
                    <Form.Item
                        name="adjustmentIndex"
                        label={translate({ key: 'contracts.adjustment-index' })}
                        labelAlign="left"
                    >
                        {!isCustomIndex ? (
                            <Select
                                allowClear
                                size={'middle'}
                                placeholder={translate({ key: 'contracts.adjustment-index-placeholder' })}
                                onChange={onAdjustmentIndexChange}
                                getPopupContainer={fixSelectOnScroll}
                            >
                                {ADJUSTMENT_INDICES.map((index) => (
                                    <Option key={index} value={index}>
                                        {index}
                                    </Option>
                                ))}
                                <Option key="other" value="other">
                                    Otro
                                </Option>
                            </Select>
                        ) : (
                            <Input
                                size={'middle'}
                                placeholder={translate({ key: 'contracts.adjustment-index-placeholder' })}
                                allowClear
                            />
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={10}>
                <Col xs={{ span: 24 }}>
                    <Form.Item name="updateMode" label={translate({ key: 'contracts.update-mode' })} labelAlign="left">
                        <Radio.Group onChange={onUpdateModeChange} value={updateMode} defaultValue="periodic">
                            <Radio value="periodic">
                                <Typography.Text>{translate({ key: 'contracts.periodic-updates' })}</Typography.Text>
                                <br />
                                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                    {translate({ key: 'contracts.periodic-updates-desc' })}
                                </Typography.Text>
                            </Radio>
                            <Radio value="predefined">
                                <Typography.Text>{translate({ key: 'contracts.predefined-amounts' })}</Typography.Text>
                                <br />
                                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                    {translate({ key: 'contracts.predefined-amounts-desc' })}
                                </Typography.Text>
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                </Col>
            </Row>
            {updateMode === 'predefined' ? (
                <div>
                    <Divider />
                    <Form.List
                        name="amountsActualizations"
                        rules={[
                            {
                                validator: (_, values, callback) => {
                                    const fieldsCount = calculatePredefinedAmounts().size;
                                    if (fieldsCount === 0) {
                                        callback();
                                        return;
                                    }
                                    const hasAllValues =
                                        Array.isArray(values) &&
                                        values.length >= fieldsCount &&
                                        Array.from({ length: fieldsCount }, (_, index) => values[index]).every(
                                            (value) => value != null && value !== 0,
                                        );
                                    if (hasAllValues) {
                                        callback();
                                        return;
                                    }
                                    callback('Ingrese todos los valores');
                                },
                            },
                        ]}
                    >
                        {(fields, actions, { errors }) => {
                            return periodicity > 0 && calculatePredefinedAmounts(errors).element;
                        }}
                    </Form.List>
                </div>
            ) : (
                <div>
                    <Row gutter={10}>
                        <Col xs={{ span: 24 }}>
                            <Form.List
                                name="amountsActualizationsNew"
                                rules={[
                                    {
                                        validator: (rule, values, callback) => {
                                            const fildsCount = calculatePeriodicAmounts().size;
                                            if (fildsCount === 0) {
                                                callback();
                                                return;
                                            }
                                            if (!values || !Array.isArray(values)) {
                                                callback('Ingresá el monto del período vigente');
                                                return;
                                            }
                                            const lastValue = values[fildsCount - 1];
                                            if (Number.isNaN(lastValue) || lastValue == null || lastValue === 0) {
                                                callback('Ingresá el monto del período vigente');
                                                return;
                                            }
                                            callback();
                                        },
                                    },
                                ]}
                            >
                                {(fields, actions, { errors }) => {
                                    return periodicity > 0 && calculatePeriodicAmounts(errors).element;
                                }}
                            </Form.List>
                        </Col>
                    </Row>
                    <div
                        style={{
                            textAlign: 'center',
                            backgroundColor: '#ededed',
                            padding: '10px',
                            lineHeight: '1.3em',
                            borderRadius: '8px',
                        }}
                    >
                        <Typography.Text>{translate({ key: 'contracts.reminder' })}</Typography.Text>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewOrOldContract;
